import Reveal from "@/components/motion/Reveal";
import KineticLines from "@/components/motion/KineticLines";
import { toast } from "sonner";
import { Sparkle, Mushroom, Spellbook, Snake, Leaf, Bat, Rose, Coffin, SpiderWeb, Potion } from "@/components/decor/SvgIcons";
import { FilePdf, ArrowRight, Lightbulb, ThermometerSimple, Plant, PawPrint, Heart } from "@phosphor-icons/react";

const CARE_SHEETS = [
  "Ball Python", "Corn Snake", "Leopard Gecko", "Crested Gecko", "Hognose Snake",
  "African Fat-Tailed Gecko", "King Snake", "Pacman Frog", "Day Gecko",
  "Blue Tongue Skink", "White's Tree Frog", "Bearded Dragon",
];

const CONSULT = [
  { icon: Plant, label: "Habitat planning" },
  { icon: Lightbulb, label: "Lighting & UVB" },
  { icon: ThermometerSimple, label: "Heating gradients" },
  { icon: PawPrint, label: "Nutrition" },
  { icon: Sparkle, label: "Bioactive setups" },
  { icon: Heart, label: "Behavior troubleshooting" },
];

const ARTICLES = [
  "Heating & UVB basics",
  "Building your first bioactive",
  "Common beginner mistakes",
  "Choosing your first reptile",
  "Enrichment ideas by species",
  "Reptile conservation 101",
];

export default function Resources() {
  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="relative pb-10 overflow-hidden">
        <div className="absolute top-10 right-10"><Spellbook size={70} /></div>
        <div className="absolute top-40 left-8 opacity-70"><Potion size={70} className="" /></div>
        <div className="absolute top-16 left-1/3 opacity-80 hidden md:block"><Bat size={54} /></div>
        <div className="absolute top-40 right-1/3 opacity-75 hidden md:block"><SpiderWeb size={64} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="chip mb-6">Husbandry Resources · Consultations</div>
          <KineticLines
            lines={["Helping reptiles", "thrive through", "education."]}
            italicIdx={1}
            className="font-display text-forest text-[12vw] md:text-[7vw] lg:text-[6rem] leading-[0.96] tracking-tight"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-lg text-forest/80">
              Care sheets, consultations, and (soon) a rehoming program — everything you need to be a
              thoughtful keeper of scaled, mossy little wonders.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Care Sheets */}
      <section className="py-20" data-testid="resources-care-sheets">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="chip mb-4">Chapter I · Field guides</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.02]">
                Animal <em className="text-plum">care sheets.</em>
              </h2>
            </div>
            <div className="chapter-num text-[10rem] hidden md:block">01</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CARE_SHEETS.map((s, i) => (
              <Reveal key={s} delay={i * 0.03}>
                <button
                  onClick={() => toast("Field guide coming soon — the ink is still drying.")}
                  className="w-full text-left grim-card p-5 flex items-center justify-between group"
                  data-testid={`care-sheet-${s.toLowerCase().replace(/\s|'|-/g, "-")}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-sage/40 flex items-center justify-center text-forest">
                      <FilePdf size={22} weight="duotone" />
                    </div>
                    <div>
                      <div className="font-display text-forest text-xl">{s}</div>
                      <div className="text-xs uppercase tracking-widest text-forest/60">Care sheet · PDF</div>
                    </div>
                  </div>
                  <ArrowRight size={18} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition" />
                </button>
              </Reveal>
            ))}
          </div>
          <div className="mt-6 text-forest/70 font-script text-2xl">…more species being scribed by candlelight.</div>
        </div>
      </section>

      {/* Consultations */}
      <section className="py-24 relative" data-testid="resources-consultations">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 relative">
              <div className="chapter-num text-[10rem] absolute -top-24 -right-6 hidden lg:block">02</div>
              <div className="chip mb-4">Chapter II · Consultations</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.02]">
                Habitat consultations, <em className="text-plum">crafted for you.</em>
              </h2>
              <p className="mt-5 text-forest/80 text-lg">
                Whether you're planning a first vivarium or troubleshooting an old friend's behavior,
                we offer both virtual and (locally) in-person consultations.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="chip">Virtual · anywhere</span>
                <span className="chip">In-person · Ventura &amp; LA County</span>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {CONSULT.map((c, i) => (
                <Reveal key={c.label} delay={i * 0.04}>
                  <div className="grim-card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-lavender/40 flex items-center justify-center text-plum">
                      <c.icon size={22} weight="duotone" />
                    </div>
                    <div className="font-display text-forest text-xl">{c.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Husbandry Articles */}
      <section className="py-24 bg-sage/15 border-y border-sage/40 relative" data-testid="resources-articles">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="chip mb-4">Chapter III · Husbandry</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.02]">
                Field notes &amp; <em className="text-plum">essays.</em>
              </h2>
            </div>
            <div className="chapter-num text-[10rem] hidden md:block">03</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.map((a, i) => (
              <Reveal key={a} delay={i * 0.05}>
                <div className="grim-card p-6 relative overflow-hidden">
                  <div className="text-xs uppercase tracking-widest text-forest/60">Article · coming soon</div>
                  <h3 className="font-display text-2xl md:text-3xl text-forest mt-3 leading-tight">{a}</h3>
                  <div className="mt-4 flex items-center gap-2 text-plum">
                    <Sparkle size={18} /> <span className="text-sm italic">brewing…</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Adoptable — coming soon */}
      <section className="py-24 relative overflow-hidden" data-testid="resources-adoptable">
        <div className="absolute top-8 right-10 opacity-70"><Snake size={100} className="rotate-12" /></div>
        <div className="absolute bottom-8 left-10 opacity-70"><Mushroom size={80} /></div>
        <div className="absolute top-12 left-14 opacity-80 hidden md:block"><Coffin size={70} /></div>
        <div className="absolute bottom-16 right-16 opacity-80 hidden md:block"><Rose size={70} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grim-card relative p-10 md:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 -z-10 opacity-40">
              <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-lavender/40 blur-3xl" />
              <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-sage/40 blur-3xl" />
            </div>
            <div className="chip mx-auto">A future chapter</div>
            <h2 className="font-display text-5xl md:text-7xl text-forest mt-5 leading-[1.02]">
              Adoptable Animals <em className="text-plum">coming soon.</em>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-forest/80 text-lg">
              We're building a thoughtful rehoming program to match reptiles and amphibians with
              educated, prepared homes. Applications and available animals will appear here first.
            </p>
            <div className="mt-8 flex items-center justify-center gap-6 opacity-80">
              <Leaf size={38} />
              <Sparkle size={18} />
              <Leaf size={28} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
